
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { NewsItem, NewsCategory } from '../types';
import { Search, Filter, X, TrendingUp, Clock, Tag, ArrowRight, ChevronDown, Zap } from 'lucide-react';

interface AdvancedSearchProps {
  items: NewsItem[];
  categories: NewsCategory[]; 
  isOpen?: boolean;
  onClose?: () => void;
  onNavigate?: (item: NewsItem) => void;
  onSearch?: (results: NewsItem[]) => void;
  selectedCategory?: string;
  onCategoryChange?: (category: string) => void;
}

const AdvancedSearch: React.FC<AdvancedSearchProps> = ({ 
    items, 
    isOpen,
    onClose,
    onNavigate,
    onSearch,
    selectedCategory: propSelectedCategory,
    onCategoryChange
}) => {
  const [query, setQuery] = useState('');
  const [internalSelectedCategory, setInternalSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('relevance');
  const [results, setResults] = useState<NewsItem[]>([]);
  
  const activeCategory = propSelectedCategory !== undefined ? propSelectedCategory : internalSelectedCategory;

  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isModal = isOpen !== undefined; 

  useEffect(() => {
    if (!isModal || !onClose) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose, isModal]);

  useEffect(() => {
    if (isModal) {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 100);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen, isModal]);

  const availableCategories = useMemo(() => {
      const cats = Array.from(new Set(items.map(i => i.category)));
      return cats.filter(c => c);
  }, [items]);

  useEffect(() => {
    let filtered = [...items].filter(n => {
      const isLocalSeoFlag = n.is_local_seo === true || (n.is_local_seo as any) === 1 || (n.is_local_seo as any) === '1';
      const hasLocalSeoTag = n.tags?.some(tag => {
        const t = tag.toLowerCase().trim();
        return t === 'local-seo' || t === 'seo-local' || t === 'seo local' || t === 'seo_local' || t === 'cluster' || t === 'pilar';
      });
      const isLocalSeoCategory = n.category?.toLowerCase().trim() === 'seo local' || n.category?.toLowerCase().trim() === 'local seo';
      
      // Show if not draft AND NOT local SEO
      return n.status !== 'draft' && !isLocalSeoFlag && !hasLocalSeoTag && !isLocalSeoCategory;
    });

    if (activeCategory !== 'all' && activeCategory !== 'Todas') { 
      filtered = filtered.filter(i => i.category === activeCategory);
    }

    if (query.trim()) {
      const q = query.toLowerCase();
      filtered = filtered.filter(i => 
        i.title.toLowerCase().includes(q) || 
        i.excerpt.toLowerCase().includes(q) ||
        i.category.toLowerCase().includes(q)
      );
    }

    if (sortBy === 'views_desc') {
        filtered.sort((a, b) => (b.views || 0) - (a.views || 0));
    } else if (sortBy === 'date_desc') {
        filtered.sort((a, b) => b.id.localeCompare(a.id));
    }

    if (onSearch) {
        onSearch(filtered);
    }

    setResults(filtered.slice(0, 8)); 
  }, [query, activeCategory, sortBy, items, onSearch]);

  const handleCategoryChange = (cat: string) => {
      if (onCategoryChange) {
          onCategoryChange(cat);
      } else {
          setInternalSelectedCategory(cat);
      }
  };

  if (isModal && !isOpen) return null;

  const containerClass = isModal 
    ? "fixed inset-0 z-[9999] flex items-start md:items-center justify-center p-3 md:p-6 pt-24 md:pt-6 bg-slate-950/40 backdrop-blur-md animate-fade-in overflow-hidden"
    : "w-full mb-8";

  const wrapperClass = isModal
    ? "bg-white w-full max-w-4xl rounded-2xl shadow-[0_32px_64px_-16px_rgba(0,0,0,0.15)] overflow-hidden flex flex-col animate-fade-in-up max-h-[85vh] md:max-h-[90vh] relative border border-gray-100"
    : "bg-white w-full rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col";

  return (
    <div className={containerClass}>
      <div 
        ref={modalRef}
        className={wrapperClass}
      >
        {/* BOTÃO DE FECHAR (X) */}
        {isModal && onClose && (
            <button 
                onClick={onClose}
                className="absolute top-4 right-4 md:top-6 md:right-6 z-30 p-2 text-gray-400 hover:bg-gray-100 hover:text-red-500 rounded-lg transition-all active:scale-90 group"
                title="Fechar Busca"
            >
                <X size={18} className="transition-transform group-hover:rotate-90" />
            </button>
        )}

        {/* CABEÇALHO COM CAMPO DE BUSCA */}
        <div className="p-5 md:p-8 pb-4 md:pb-2 shrink-0">
            <div className="flex items-center justify-between mb-4">
                <span className="text-[10px] font-black text-primary uppercase tracking-[0.2em] ml-1">
                    {isModal ? "Explore o Conhecimento" : "O que você busca?"}
                </span>
            </div>
            <div className="relative flex items-center bg-gray-50 border border-gray-100 rounded-xl p-1 px-4 md:px-6 shadow-inner focus-within:bg-white focus-within:border-primary/20 focus-within:shadow-md transition-all group">
                <Search size={18} className="text-gray-300 group-focus-within:text-primary transition-colors" />
                <input 
                    ref={inputRef}
                    type="text" 
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Busque por temas, ferramentas ou serviços..."
                    className="flex-1 px-3 md:px-4 py-3 md:py-3.5 text-sm md:text-base font-bold text-gray-800 outline-none bg-transparent placeholder:text-gray-300"
                />
                {isModal && (
                    <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-gray-100 text-[9px] font-black text-gray-400 uppercase tracking-widest">
                        <Filter size={12} /> Filtros
                    </div>
                )}
            </div>
        </div>

        {/* CONTEÚDO EM DUAS COLUNAS */}
        <div className={`flex flex-col md:flex-row flex-1 overflow-hidden ${!isModal ? 'min-h-[400px]' : ''}`}>
            
            {/* COLUNA: FILTROS */}
            <aside className="w-full md:w-[200px] p-5 md:p-8 pt-2 md:pt-8 border-b md:border-b-0 md:border-r border-gray-50 flex flex-col gap-6 shrink-0 overflow-y-auto no-scrollbar md:max-h-none max-h-[120px]">
                <div>
                    <h4 className="flex items-center gap-2 text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3">
                        <Tag size={10} className="text-primary/50" /> Categorias
                    </h4>
                    <div className="flex flex-row md:flex-wrap gap-2 overflow-x-auto md:overflow-visible pb-2 md:pb-0 no-scrollbar">
                        <button 
                            onClick={() => handleCategoryChange('all')}
                            className={`px-3 py-1.5 rounded-lg text-[9px] font-black transition-all border whitespace-nowrap uppercase tracking-wider ${activeCategory === 'all' || activeCategory === 'Todas' ? 'bg-primary text-white border-primary shadow-sm' : 'bg-gray-50 text-gray-400 border-gray-100 hover:bg-gray-100'}`}
                        >
                            Todas
                        </button>
                        {availableCategories.map(cat => (
                            <button 
                                key={cat}
                                onClick={() => handleCategoryChange(cat)}
                                className={`px-3 py-1.5 rounded-lg text-[9px] font-black transition-all border whitespace-nowrap uppercase tracking-wider ${activeCategory === cat ? 'bg-primary text-white border-primary shadow-sm' : 'bg-white text-gray-400 border-gray-100 hover:border-gray-200'}`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="hidden md:block">
                    <h4 className="flex items-center gap-2 text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3">
                        <TrendingUp size={10} className="text-primary/50" /> Ordenação
                    </h4>
                    <div className="relative">
                        <select 
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as any)}
                            className="w-full bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 text-[10px] font-black text-gray-600 outline-none appearance-none cursor-pointer focus:border-primary/30 transition-all uppercase tracking-widest"
                        >
                            <option value="relevance">Relevância</option>
                            <option value="views_desc">Mais Vistos</option>
                            <option value="date_desc">Mais Novos</option>
                        </select>
                        <ChevronDown size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                </div>
            </aside>

            {/* COLUNA: RESULTADOS */}
            <main className="flex-1 p-5 md:p-8 overflow-y-auto custom-scrollbar bg-gray-50/10">
                <div className="flex items-center justify-between mb-4">
                    <h4 className="text-[9px] font-black text-gray-300 uppercase tracking-[0.2em]">
                        {query ? 'Resultados' : 'Mais Buscados'}
                    </h4>
                    <span className="text-[9px] font-bold text-gray-300">{results.length} sugestões</span>
                </div>

                {results.length > 0 ? (
                    <div className="grid grid-cols-1 gap-3">
                        {results.map(item => (
                            <div 
                                key={item.id}
                                onClick={() => onNavigate?.(item)}
                                className="group flex items-center gap-4 p-3 rounded-xl bg-white border border-transparent hover:border-primary/10 hover:shadow-md transition-all cursor-pointer shadow-sm"
                            >
                                <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg overflow-hidden bg-gray-100 shrink-0 border border-gray-50">
                                    <img src={item.imageUrl} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h5 className="text-xs md:text-sm font-black text-gray-900 group-hover:text-primary transition-colors leading-tight mb-1 line-clamp-1">
                                        {item.title}
                                    </h5>
                                    <div className="flex items-center gap-3">
                                        <span className="text-[8px] font-black text-primary bg-primary/5 px-1.5 py-0.5 rounded-md uppercase tracking-wider border border-primary/10">
                                            {item.category}
                                        </span>
                                        <span className="flex items-center gap-1 text-[8px] text-gray-400 font-bold">
                                            <Clock size={8} /> {item.date}
                                        </span>
                                    </div>
                                </div>
                                <div className="hidden md:flex w-8 h-8 rounded-lg bg-gray-50 text-gray-300 items-center justify-center group-hover:bg-primary group-hover:text-white transition-all transform group-hover:translate-x-1">
                                    <ArrowRight size={14} />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="py-16 flex flex-col items-center justify-center text-center">
                        <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center text-gray-200 mb-3">
                            <Search size={24} />
                        </div>
                        <p className="font-black uppercase tracking-widest text-[9px] text-gray-400">Nenhum resultado encontrado</p>
                        <p className="text-[10px] text-gray-300 mt-1">Tente palavras-chave mais genéricas.</p>
                    </div>
                )}
            </main>
        </div>

        {/* RODAPÉ DO MODAL */}
        {isModal && (
            <div className="bg-gray-50/50 px-6 md:px-8 py-3.5 border-t border-gray-100 flex justify-between items-center text-[8px] font-black text-gray-400 uppercase tracking-[0.2em] shrink-0">
                <div className="flex items-center gap-2">
                    <Zap size={10} className="text-primary/40" />
                    <span>Algoritmo v2.0 Smart-Search</span>
                </div>
                <div className="hidden md:flex items-center gap-4">
                    <span className="opacity-50">ESC para fechar</span>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default AdvancedSearch;
